import { useQuery } from '@tanstack/react-query';
import { api } from '../../api/client';
import { useCursorPages } from '../../lib/useCursorPages';
import type { CurrencyAmount, Page, PortalSummary } from '../agencies/agencyApi';

/**
 * The agency portal's own data. Every URL is under /admin/agency-portal, which
 * the API scopes to the signed-in agency — no page ever names an agency id, so
 * an agency can only ever see its own account.
 */
export const PORTAL_BASE = '/admin/agency-portal';
export const PORTAL_KEY = ['admin', 'agency-portal'] as const;

export type ListenerStatus = 'pending' | 'interview_scheduled' | 'approved' | 'rejected';
/** male | female | other | undisclosed (string so a new value never breaks the page). */
export type Gender = string;

export interface PortalListenerRow {
  id: string;
  userId: string;
  handle: string;
  displayName: string;
  photoUrl: string | null;
  bio: string;
  gender: Gender;
  tier: string;
  status: ListenerStatus;
  languages: string[];
  expertiseTags: string[];
  isSuspended: boolean;
  suspendedUntil: string | null;
  rejectionReason: string | null;
  ratingAvg: number;
  createdAt: string;
  blocked: boolean;
  deleted: boolean;
  /** Status only — the documents stay with the Dude team. */
  kyc: 'none' | 'uploaded' | 'verified' | 'rejected';
  voice: 'none' | 'pending_review' | 'verified' | 'rejected';
  calls: number;
  lastCallAt: string | null;
}

export interface PortalUserRow {
  userId: string;
  handle: string;
  displayName: string | null;
  gender: Gender;
  joinedAt: string;
  attributedAt: string;
  source: 'manual' | 'link' | 'admin';
  deleted: boolean;
  blocked: boolean;
  blockedAt: string | null;
  blockReason: string | null;
  recharges: CurrencyAmount[];
  lastRechargeAt: string | null;
  calls: number;
  lastCallAt: string | null;
}

export interface PeopleStats {
  people: {
    users: number;
    blockedUsers: number;
    listeners: { pending: number; approved: number; rejected: number };
    openTickets: number;
  };
}

export function usePortalSummary() {
  return useQuery({
    queryKey: [...PORTAL_KEY, 'summary'],
    queryFn: async () => (await api.get<PortalSummary>(`${PORTAL_BASE}/summary`)).data,
  });
}

export function usePeopleStats() {
  return useQuery({
    queryKey: [...PORTAL_KEY, 'people-stats'],
    queryFn: async () => (await api.get<PeopleStats>(`${PORTAL_BASE}/people-stats`)).data,
    refetchInterval: 30_000,
  });
}

export function usePortalListeners(filter: { status?: ListenerStatus; q?: string }) {
  return useCursorPages<PortalListenerRow>([...PORTAL_KEY, 'listeners', filter], async (cursor) =>
    (
      await api.get<Page<PortalListenerRow>>(`${PORTAL_BASE}/listeners`, {
        params: { ...filter, q: filter.q || undefined, cursor, limit: 50 },
      })
    ).data,
  );
}

export function usePortalUsers(filter: { status?: 'active' | 'blocked'; q?: string }) {
  return useCursorPages<PortalUserRow & { id: string }>([...PORTAL_KEY, 'people', filter], async (cursor) => {
    const page = (
      await api.get<Page<PortalUserRow>>(`${PORTAL_BASE}/people`, {
        params: { ...filter, q: filter.q || undefined, cursor, limit: 50 },
      })
    ).data;
    return { items: page.items.map((r) => ({ ...r, id: r.userId })), nextCursor: page.nextCursor };
  });
}
